import { useContext, useEffect, useState } from "react";
import {
	Controller,
	SubmitHandler,
	useFieldArray,
	useForm,
} from "react-hook-form";
import Label from "../../../../../components/form_fields/Label";
import PhoneInput from "react-phone-input-2";
// import "react-phone-input-2/lib/style.css";
import { isValidPhoneNumber, CountryCode } from "libphonenumber-js";
import { GENDER } from "../../../../../models/employee.dto";
import APISelectField from "../../../../../components/form_fields/APISelectField";
import { EmployeeFormContext } from "../../EmployeeFormContext";
import useCompanyStore from "../../../../../state-management/useCompanyStore";
import useUserStore from "../../../../../state-management/useUserStore";
import { PATHS } from "../../../../../routes/routes.paths";
import { Typography } from "@mui/material";
import { FaQuestionCircle } from "react-icons/fa";
import { BsTrash } from "react-icons/bs";
import {
	CustomizedTooltip,
	APP_CONFIG,
} from "../../../../../helpers/appHelpers";
import { useGetIdType } from "../../../../../services/identity-type-document.service";
import { useGetCountry } from "../../../../../services/country.service";

interface IPersonalDetails {
	formData: any;
	setFormData: React.Dispatch<React.SetStateAction<any>>;
	setView: (view: string) => void;
	reloadTableData?: () => void;
	closeForm?: () => void;
}

interface IFormInputs {
	photoUrl?: string;
	firstName: string;
	lastName: string;
	otherNames?: string;
	dateOfBirth: string;
	gender: GENDER;
	title?: string;
	privateEmail: string;
	email: string;
	msisdn: string;
	alternateMsisdn: string;
	majorGradeLevelId?: number;
	minorGradeLevelId?: number;
	nationality: string;
	regionId: string;
	resident: boolean;
	tribeId: number;
	ssn?: string;
	reemployed: boolean;
	address: string;
	digitalAddress: string;
	taxIdentificationNumber: string;
	unionMember?: boolean;
	idInfo: {
		id?: number;
		typeId: number;
		issuingCountry: string;
		value: string;
	}[];
	removeIdInfoIds: any;
}

const PersonalDetails = ({
	formData,
	setView,
	setFormData,
	reloadTableData,
	closeForm,
}: IPersonalDetails) => {
	const { data: idTypesResponse, isLoading, error } = useGetIdType();
	const { userType } = useUserStore();
	const { currentCompany } = useCompanyStore();
	const [associatedCompanyLevels, setAssociatedCompanyLevels] = useState<any>(
		{},
	);

	// Fetch the company's country to get minimumEmploymentAge and taxNumberRequired.
	// useGetCountry returns a paginated list, so find the matching country by ID.
	const { data: countriesData } = useGetCountry({
		limit: APP_CONFIG.PAGE_LIMIT,
	});
	const companyCountry = countriesData?.data?.find(
		(c) => c.id === currentCompany?.countryId,
	);
	const minimumEmploymentAge = companyCountry?.minimumEmploymentAge;
	const taxNumberRequired = companyCountry?.taxNumberRequired;

	// Track selected country for each phone field so validation matches the flag chosen.
	// Initialise from existing formData so edits pre-populate correctly.
	const [msisdnCountry, setMsisdnCountry] = useState("gh");
	const [alternateMsisdnCountry, setAlternateMsisdnCountry] = useState("gh");

	const {
		nationalities,
		MajorGradeLevelData,
		MinorGradeLevelData,
		RegionData,
		TribeData,
	} = useContext(EmployeeFormContext);
	const remappedIdInfo =
		formData.idInfo?.map((item) => ({
			id: item.id,
			typeId: item.typeId,
			issuingCountry: item.issuingCountry,
			value: item.value,
		})) ?? [];

	// Normalize resident to a string so the select can match it against "yes"/"no" options.
	// The API may return a boolean (true/false) or a string ("yes"/"no") depending on context.
	const normalizeResident = (value: any): string => {
		if (value === true || value === "yes") return "yes";
		if (value === false || value === "no") return "no";
		return "";
	};

	const {
		register,
		watch,
		handleSubmit,
		control,
		setValue,
		formState: { errors },
	} = useForm<IFormInputs>({
		defaultValues: {
			...formData,
			dateOfBirth: formData?.dateOfBirth?.slice(0, 10) ?? "",
			removeIdInfoIds: formData.removeIdInfoIds ?? [],
			idInfo: remappedIdInfo,
			resident: normalizeResident(formData?.resident),
		},
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: "idInfo",
	});

	useEffect(() => {
		const { unsubscribe } = watch((values) => {
			setFormData((prev) => ({
				...prev,
				...values,
				removeIdInfoIds: prev.removeIdInfoIds ?? [],
			}));
		});
		return () => unsubscribe();
	}, [watch]);

	useEffect(() => {
		if (formData?.majorGradeLevelId) {
			const gradeLevel = MajorGradeLevelData.find(
				(level) => level.id === formData.majorGradeLevelId,
			);
			setAssociatedCompanyLevels((prev) => ({
				...prev,
				major: gradeLevel?.companyLevel?.levelName,
			}));
		} else {
			setAssociatedCompanyLevels((prev) => ({ ...prev, major: undefined }));
		}
	}, [formData?.majorGradeLevelId]);

	useEffect(() => {
		if (formData?.minorGradeLevelId) {
			const gradeLevel = MinorGradeLevelData.find(
				(level) => level.id === formData.minorGradeLevelId,
			);
			setAssociatedCompanyLevels((prev) => ({
				...prev,
				minor: gradeLevel?.companyLevel?.levelName,
			}));
		} else {
			setAssociatedCompanyLevels((prev) => ({ ...prev, minor: undefined }));
		}
	}, [formData?.minorGradeLevelId]);

	const handleRemoveIdInfo = (index: number) => {
		const databaseId = watch(`idInfo.${index}.id`);

		if (databaseId && typeof databaseId === "number") {
			setFormData((prev) => ({
				...prev,
				removeIdInfoIds: [...(prev.removeIdInfoIds ?? []), databaseId],
			}));
		}

		remove(index);
	};

	const onSubmit: SubmitHandler<IFormInputs> = (data) => {
		const cleanedIdInfo = data.idInfo?.map(({ id, ...rest }) => rest) || [];

		const updatedData = {
			...data,
			firstName: data.firstName?.trim(),
			lastName: data.lastName?.trim(),
			idInfo: cleanedIdInfo,
			removeIdInfoIds: formData.removeIdInfoIds ?? [],
			// PhoneInput passes null when the field is empty (API returns null for
			// unset phone numbers). The backend requires a string or no field at all,
			// so coerce to a string when a value exists, or drop the field entirely.
			msisdn: data.msisdn ? String(data.msisdn) : undefined,
			alternateMsisdn: data.alternateMsisdn
				? String(data.alternateMsisdn)
				: undefined,
			// If email was cleared (had a value before, now empty), send removeEmail: true
			// so the backend knows to unset it rather than ignoring the empty string.
			removeEmail:
				formData?.email && !data.email ? true : undefined,
		};
		setFormData(updatedData);
		setView("employmentDetails");
	};

	// Returns a warning string if the number looks invalid for the selected country,
	// or null if valid / incomplete. Does not block submission.
	const getPhoneWarning = (value: string, country: string): string | null => {
		if (!value) return null;
		try {
			return isValidPhoneNumber(
				`+${value}`,
				country.toUpperCase() as CountryCode,
			)
				? null
				: "This number may not be valid for the selected country";
		} catch {
			return null; // don't warn on incomplete / partial input
		}
	};

	const msisdnWarning = getPhoneWarning(watch("msisdn"), msisdnCountry);
	const alternateMsisdnWarning = getPhoneWarning(
		watch("alternateMsisdn"),
		alternateMsisdnCountry,
	);

	const validateSSN = (value: any): true | string => {
		if (!value) return true;
		const cleaned = String(value).replace(/\s/g, "");
		if (cleaned.length < 9 || cleaned.length > 15)
			return "SSN must be between 9 and 15 characters";
		return true;
	};

	// Today's date as YYYY-MM-DD — used as the `max` attribute on the date
	// picker to disable future date selection in the browser UI, and also
	// referenced in validateDateOfBirth below.
	const todayISO = new Date().toISOString().split("T")[0];

	const validateDateOfBirth = (value: string): true | string => {
		if (!value) return true;

		const dob = new Date(value);
		const today = new Date();
		today.setHours(0, 0, 0, 0);

		if (dob >= today) {
			return "Date of birth cannot be today or a future date";
		}

		if (minimumEmploymentAge) {
			const maxDOB = new Date(today);
			maxDOB.setFullYear(today.getFullYear() - minimumEmploymentAge);
			if (dob > maxDOB) {
				return `Employee must be at least ${minimumEmploymentAge} years old`;
			}
		}

		return true;
	};

	if (isLoading) {
		return <div>Loading ID Types...</div>;
	}

	if (error) {
		return <div>Error fetching ID Types: {error.message}</div>;
	}

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<div className="grid grid-cols-2 gap-x-4 gap-y-6">
				<div className="w-full">
					<Label title="First Name" for="firstName" />
					<input
						defaultValue={formData?.firstName}
						placeholder={"Enter first name"}
						type="text"
						{...register("firstName", {
							required: "Enter first name here",
							// Trim the value before the `required` check runs so
							// whitespace-only input (" ") is treated as empty
							// instead of slipping through to the backend.
							setValueAs: (value) =>
								typeof value === "string" ? value.trim() : value,
							validate: (value) =>
								(typeof value === "string" && value.trim().length > 0) ||
								"Enter first name here",
						})}
						id="firstName"
						className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
							errors.firstName
								? " border border-red-500 focus:border-red-500 focus:outline-red-500"
								: "border-none"
						}`}
					/>
					{errors.firstName && (
						<p className="text-red-500 text-sm mt-1">
							{errors.firstName.message}
						</p>
					)}
				</div>
				<div className="w-full">
					<Label title="Last Name" for="lastName" />
					<input
						defaultValue={formData?.lastName}
						placeholder={"Enter last name"}
						type="text"
						{...register("lastName", {
							required: "Enter last name here",
							// Trim the value before the `required` check runs so
							// whitespace-only input (" ") is treated as empty
							// instead of slipping through to the backend.
							setValueAs: (value) =>
								typeof value === "string" ? value.trim() : value,
							validate: (value) =>
								(typeof value === "string" && value.trim().length > 0) ||
								"Enter last name here",
						})}
						id="lastName"
						className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
							errors.lastName
								? " border border-red-500 focus:border-red-500 focus:outline-red-500"
								: "border-none"
						}`}
					/>
					{errors.lastName && (
						<p className="text-red-500 text-sm mt-1">
							{errors.lastName.message}
						</p>
					)}
				</div>
				<div className="w-full">
					<Label title="Other Names" for="otherNames" optional />
					<input
						defaultValue={formData?.otherNames}
						placeholder={"Enter other names"}
						type="text"
						{...register("otherNames", {
							required: false,
						})}
						id="otherNames"
						className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
							errors.otherNames
								? " border border-red-500 focus:border-red-500 focus:outline-red-500"
								: "border-none"
						}`}
					/>
					{errors.otherNames && (
						<p className="text-red-500 text-sm mt-1">
							{errors.otherNames.message}
						</p>
					)}
				</div>
				<div className="w-full">
					<Label title="Date of Birth" for="dateOfBirth" />
					<input
						placeholder="mm/dd/yyyy"
						type="date"
						max={todayISO}
						{...register("dateOfBirth", {
							required: "Enter date of birth here",
							validate: validateDateOfBirth,
						})}
						id="dateOfBirth"
						className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
							errors.dateOfBirth
								? " border border-red-500 focus:border-red-500 focus:outline-red-500"
								: "border-none"
						}`}
					/>
					{errors.dateOfBirth && (
						<p className="text-red-500 text-sm mt-1">
							{errors.dateOfBirth.message}
						</p>
					)}
				</div>
				<div className="w-full">
					<Label title="Gender" for="gender" />
					<select
						defaultValue={formData?.gender}
						className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
							errors.gender
								? " border border-red-500 focus:border-red-500 focus:outline-red-500"
								: "border-none"
						}`}
						{...register("gender", {
							required: "Select gender here",
						})}
					>
						<option value="">Select Gender</option>
						<option value="MALE">Male</option>
						<option value="FEMALE">Female</option>
					</select>
					{errors.gender && (
						<p className="text-red-500 text-sm mt-1">{errors.gender.message}</p>
					)}
				</div>
				<div className="w-full">
					<Label title="Title" for="title" optional />
					<select
						defaultValue={formData?.title}
						className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
							errors.title
								? " border border-red-500 focus:border-red-500 focus:outline-red-500"
								: "border-none"
						}`}
						{...register("title", {
							required: false,
						})}
					>
						<option value="">Select Title</option>
						<option value="Master">Master</option>
						<option value="Miss">Miss</option>
						<option value="Mr.">Mr.</option>
						<option value="Mrs.">Mrs.</option>
						<option value="Prof.">Prof.</option>
						<option value="Dr.">Dr.</option>
						<option value="Rev.">Rev.</option>
					</select>
					{errors.title && (
						<p className="text-red-500 text-sm mt-1">{errors.title.message}</p>
					)}
				</div>
				<div className="w-full">
					<div className="flex items-center gap-1.5">
						<Label title="Corporate Email" for="email" optional />
						{currentCompany?.enableEmployeeLogin && (
							<CustomizedTooltip
								title="Updating this email will also update the employee's username on their account."
								placement="top"
								arrow
							>
								<Typography>
									<FaQuestionCircle className="text-black" />
								</Typography>
							</CustomizedTooltip>
						)}
					</div>
					<div className="relative">
						<input
							defaultValue={formData?.email ?? ""}
							placeholder={"Enter email address"}
							type="email"
							{...register("email", {
								required: false,
								pattern: {
									value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
									message: "Invalid email address",
								},
							})}
							id="email"
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 pr-12 mt-2 text-sm  ${
								errors.email
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						/>
						{watch("email") && (
							<button
								type="button"
								onClick={() => {
									setValue("email", "");
									setFormData((prev) => ({ ...prev, email: "" }));
								}}
								className="absolute right-3 top-1/2 translate-y-[-25%] text-gray-400 hover:text-red-500 transition-colors"
							>
								<BsTrash className="text-lg" />
							</button>
						)}
					</div>
					{errors.email && (
						<p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
					)}
				</div>
				<div className="w-full">
					<Label title="Private Email" for="privateEmail" optional />
					<div className="relative">
						<input
							defaultValue={formData?.privateEmail ?? ""}
							placeholder={"Enter email address"}
							type="email"
							{...register("privateEmail", {
								required: false,
								pattern: {
									value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
									message: "Invalid email address",
								},
							})}
							id="privateEmail"
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 pr-12 mt-2 text-sm  ${
								errors.privateEmail
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						/>
						{watch("privateEmail") && (
							<button
								type="button"
								onClick={() => {
									setValue("privateEmail", "");
									setFormData((prev) => ({ ...prev, privateEmail: "" }));
								}}
								className="absolute right-3 top-1/2 translate-y-[-25%] text-gray-400 hover:text-red-500 transition-colors"
							>
								<BsTrash className="text-lg" />
							</button>
						)}
					</div>
					{errors.privateEmail && (
						<p className="text-red-500 text-sm mt-1">
							{errors.privateEmail.message}
						</p>
					)}
				</div>
				<div className="w-full">
					<Label title="Telephone" for="msisdn" optional />
					<Controller
						name={"msisdn"}
						control={control}
						rules={{
							required: false,
						}}
						render={({ field }) => (
							<div
								className={`w-full bg-secondary-gray rounded-lg mt-2 flex items-center ${
									errors.msisdn
										? "border border-red-500 focus:border-red-500 focus:outline-red-500"
										: "border-none"
								}`}
							>
								<PhoneInput
									{...field}
									country={msisdnCountry}
									value={formData?.msisdn ?? ""}
									onChange={(value, countryData: any) => {
										field.onChange(value);
										setMsisdnCountry(countryData.countryCode);
									}}
									inputStyle={{
										height: "50px",
										border: "none",
										backgroundColor: "#F2F2F4",
										color: "black",
										boxShadow: "none",
										width: "95%",
										margin: "0px 0px 0px 15px",
										borderRadius: "10px",
									}}
									containerStyle={{
										height: "50px",
										border: "none",
										backgroundColor: "#F2F2F4",
										boxShadow: "none",
										borderRadius: "10px",
									}}
									buttonStyle={{
										border: "none",
										backgroundColor: "#F2F2F4",
										boxShadow: "none",
										padding: "0px 10px",
									}}
								/>
							</div>
						)}
					/>
					{errors.msisdn && (
						<p className="text-red-500 text-sm mt-1">{errors.msisdn.message}</p>
					)}
					{!errors.msisdn && msisdnWarning && (
						<p className="text-amber-500 text-sm mt-1">{msisdnWarning}</p>
					)}
				</div>
				<div className="w-full">
					<label
						htmlFor="alternateMsisdn"
						className="text-secondary-darkgray text-sm"
					>
						Telephone <i className="font-bold">(optional)</i>
					</label>
					<Controller
						name={"alternateMsisdn"}
						control={control}
						defaultValue=""
						rules={{
							required: false,
						}}
						render={({ field }) => (
							<div
								className={`w-full bg-secondary-gray rounded-lg mt-2 flex items-center ${
									errors.alternateMsisdn
										? "border border-red-500 focus:border-red-500 focus:outline-red-500"
										: "border-none"
								}`}
							>
								<PhoneInput
									{...field}
									country={alternateMsisdnCountry}
									value={formData?.alternateMsisdn ?? ""}
									onChange={(value, countryData: any) => {
										field.onChange(value);
										setAlternateMsisdnCountry(countryData.countryCode);
									}}
									inputStyle={{
										height: "50px",
										border: "none",
										backgroundColor: "#F2F2F4",
										color: "black",
										boxShadow: "none",
										width: "95%",
										margin: "0px 0px 0px 15px",
										borderRadius: "10px",
									}}
									containerStyle={{
										height: "50px",
										border: "none",
										backgroundColor: "#F2F2F4",
										boxShadow: "none",
										borderRadius: "10px",
									}}
									buttonStyle={{
										border: "none",
										backgroundColor: "#F2F2F4",
										boxShadow: "none",
										padding: "0px 10px",
									}}
								/>
							</div>
						)}
					/>
					{errors.alternateMsisdn && (
						<p className="text-red-500 text-sm mt-1">
							{errors.alternateMsisdn.message}
						</p>
					)}
					{!errors.alternateMsisdn && alternateMsisdnWarning && (
						<p className="text-amber-500 text-sm mt-1">
							{alternateMsisdnWarning}
						</p>
					)}
				</div>
				<div className="w-full">
					<Label title="Grade Major Level" for="majorGradeLevelId" optional />
					<APISelectField
						key="majorGradeLevelId"
						attributes={{
							id: "majorGradeLevelId",
							defaultValue: formData?.majorGradeLevelId,
						}}
						placeholder="Select Level"
						register={register}
						name="majorGradeLevelId"
						options={MajorGradeLevelData}
						pathname={PATHS.SUB_COMPANY_SETTINGS.GRADE_LEVELS}
						required={false}
						error={errors.majorGradeLevelId}
						valueAsNumber
						doNotLinkToResource={userType === "HR"}
					/>
					{associatedCompanyLevels?.major && (
						<p className="text-xs mt-1">
							Associated Company Level:{" "}
							<span className="font-semibold">
								{associatedCompanyLevels?.major}
							</span>
						</p>
					)}
					{errors.majorGradeLevelId && (
						<p className="text-red-500 text-sm mt-1">
							{errors.majorGradeLevelId.message}
						</p>
					)}
				</div>
				<div className="w-full">
					<Label title="Grade Minor Level" for="minorGradeLevelId" optional />
					<APISelectField
						key="minorGradeLevelId"
						attributes={{
							id: "minorGradeLevelId",
							defaultValue: formData?.minorGradeLevelId,
						}}
						placeholder="Select Level"
						register={register}
						name="minorGradeLevelId"
						options={MinorGradeLevelData}
						pathname={PATHS.SUB_COMPANY_SETTINGS.GRADE_LEVELS}
						required={false}
						error={errors.minorGradeLevelId}
						valueAsNumber
						doNotLinkToResource={userType === "HR"}
					/>
					{associatedCompanyLevels?.minor && (
						<p className="text-xs mt-1">
							Associated Company Level:{" "}
							<span className="font-semibold">
								{associatedCompanyLevels?.minor}
							</span>
						</p>
					)}
					{errors.minorGradeLevelId && (
						<p className="text-red-500 text-sm mt-1">
							{errors.minorGradeLevelId.message}
						</p>
					)}
				</div>
				<div className="w-full">
					<Label title="Nationality" for="nationality" />
					<select
						defaultValue={formData?.nationality}
						className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
							errors.nationality
								? " border border-red-500 focus:border-red-500 focus:outline-red-500"
								: "border-none"
						}`}
						{...register("nationality", {
							required: "Select nationality here",
						})}
					>
						<option value="">Select Nationality</option>
						{nationalities?.map((nationality) => (
							<option key={nationality.id} value={nationality.name}>
								{nationality.name}
							</option>
						))}
					</select>
					{errors.nationality && (
						<p className="text-red-500 text-sm mt-1">
							{errors.nationality.message}
						</p>
					)}
				</div>
				<div className="w-full">
					<Label title="Region" for="regionId" optional />
					<APISelectField
						key="regionId"
						attributes={{
							id: "regionId",
							defaultValue: formData?.regionId,
						}}
						placeholder="Select Region"
						register={register}
						name="regionId"
						options={RegionData}
						pathname={PATHS.SUB_COMPANY_SETTINGS.REGIONS}
						required={false}
						error={errors.regionId}
						valueAsNumber
						doNotLinkToResource={userType === "HR"}
					/>
					{errors.regionId && (
						<p className="text-red-500 text-sm mt-1">
							{errors.regionId.message}
						</p>
					)}
				</div>
				<div className="w-full">
					<Label title="Resident?" for="resident" optional />
					<select
						className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
							errors.resident
								? " border border-red-500 focus:border-red-500 focus:outline-red-500"
								: "border-none"
						}`}
						{...register("resident", {
							required: false,
						})}
					>
						<option value="">Select Option</option>
						<option value="yes">Yes</option>
						<option value="no">No</option>
					</select>
					{errors.resident && (
						<p className="text-red-500 text-sm mt-1">
							{errors.resident.message}
						</p>
					)}
				</div>
				<div className="w-full">
					<Label title="Tribe" for="tribeId" optional />
					<APISelectField
						key="tribeId"
						attributes={{
							id: "tribeId",
							defaultValue: formData?.tribeId,
						}}
						placeholder="Select Tribe"
						register={register}
						name="tribeId"
						options={TribeData}
						pathname={PATHS.SUB_COMPANY_SETTINGS.TRIBES}
						required={false}
						error={errors.tribeId}
						valueAsNumber
						doNotLinkToResource={userType === "HR"}
					/>
					{errors.tribeId && (
						<p className="text-red-500 text-sm mt-1">
							{errors.tribeId.message}
						</p>
					)}
				</div>
				<div className="w-full">
					<Label title="Address" for="address" optional />
					<input
						defaultValue={formData?.address}
						placeholder={"Enter employee address"}
						type="text"
						{...register("address", {
							required: false,
						})}
						id="address"
						className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
							errors.address
								? " border border-red-500 focus:border-red-500 focus:outline-red-500"
								: "border-none"
						}`}
					/>
				</div>
				<div className="w-full">
					<Label title="Digital Address" for="digitalAddress" optional />
					<input
						defaultValue={formData?.digitalAddress}
						placeholder={"Enter digital address"}
						type="text"
						{...register("digitalAddress", {
							required: false,
						})}
						id="digitalAddress"
						className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
							errors.digitalAddress
								? " border border-red-500 focus:border-red-500 focus:outline-red-500"
								: "border-none"
						}`}
					/>
				</div>
				<div className="w-full">
					<Label title="Social Security Number" for="ssn" optional />
					<input
						defaultValue={formData?.ssn}
						placeholder={"******"}
						type="text"
						{...register("ssn", {
							required: false,
							validate: validateSSN,
						})}
						id="ssn"
						className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
							errors.ssn
								? " border border-red-500 focus:border-red-500 focus:outline-red-500"
								: "border-none"
						}`}
					/>
					{errors.ssn && (
						<p className="text-red-500 text-sm mt-1">{errors.ssn.message}</p>
					)}
				</div>
				<div className="w-full">
					<Label
						title="Tax Identification Number"
						for="taxIdentificationNumber"
						optional
					/>
					<input
						defaultValue={formData?.taxIdentificationNumber}
						placeholder={"******"}
						type="text"
						{...register("taxIdentificationNumber", {
							required: false,
						})}
						id="taxIdentificationNumber"
						className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
							errors.taxIdentificationNumber
								? "border border-red-500 focus:border-red-500 focus:outline-red-500"
								: "border-none"
						}`}
					/>
					{errors.taxIdentificationNumber && (
						<p className="text-red-500 text-sm mt-1">
							{errors.taxIdentificationNumber.message}
						</p>
					)}
				</div>

				<div className="col-span-2">
					<Label title="ID Information" for="idInfo" optional />

					<div className="space-y-4">
						{fields.map((field, index) => (
							<div key={field.id} className="flex space-x-2">
								<select
									{...register(`idInfo.${index}.typeId`)}
									defaultValue={field.typeId}
									className={`bg-secondary-gray rounded-lg h-14 px-5 text-sm flex-1 ${
										!watch(`idInfo.${index}.typeId`)
											? "text-gray-400"
											: "text-black"
									}`}
								>
									<option value="" disabled hidden>
										Select ID Type
									</option>
									{idTypesResponse?.data.map((type) => (
										<option
											key={type.id}
											value={type.id}
											className="text-black"
										>
											{type.name}
										</option>
									))}
								</select>

								<select
									{...register(`idInfo.${index}.issuingCountry`)}
									defaultValue={field.issuingCountry}
									className="bg-secondary-gray rounded-lg h-14 px-5 text-sm flex-1"
								>
									<option value="" disabled hidden>
										Select Issuing Country
									</option>
									{nationalities.map((nationality) => (
										<option
											key={nationality.countryCode}
											value={nationality.countryCode}
										>
											{nationality.name}
										</option>
									))}
								</select>

								<input
									{...register(`idInfo.${index}.value`)}
									defaultValue={field.value}
									placeholder="ID Value"
									className="bg-secondary-gray rounded-lg h-14 px-5 text-sm flex-1"
								/>

								<button
									type="button"
									onClick={() => handleRemoveIdInfo(index)}
									className="bg-red-500 text-white rounded-lg px-4"
								>
									Remove
								</button>
							</div>
						))}
					</div>

					<button
						type="button"
						onClick={() => append({ typeId: 0, issuingCountry: "", value: "" })}
						className="bg-primary-mango text-white rounded-lg px-4 py-2 mt-2"
					>
						Add ID
					</button>
				</div>

				<div className="col-span-2 flex space-x-4">
					<h3>Re-employed?</h3>
					<div className="flex space-x-3 items-center">
						<input
							defaultChecked={
								formData?.reemployed === true || formData?.reemployed === "yes"
							}
							{...register("reemployed", {
								required: false,
							})}
							type="radio"
							id="reemployed-yes"
							value="yes"
						/>
						<label htmlFor="reemployed-yes">Yes</label>
					</div>
					<div className="flex space-x-3 items-center">
						<input
							defaultChecked={
								formData?.reemployed === false || formData?.reemployed === "no"
							}
							{...register("reemployed", {
								required: false,
							})}
							type="radio"
							id="reemployed-no"
							value="no"
						/>
						<label htmlFor="reemployed-no">No</label>
					</div>
				</div>
				<div className="col-span-2 flex space-x-4">
					<h3>Union member?</h3>
					<div className="flex space-x-3 items-center">
						<input
							defaultChecked={
								formData?.unionMember === true ||
								formData?.unionMember === "yes"
							}
							{...register("unionMember", {
								required: false,
							})}
							type="radio"
							id="unionMember-yes"
							value="yes"
						/>
						<label htmlFor="unionMember-yes">Yes</label>
					</div>
					<div className="flex space-x-3 items-center">
						<input
							defaultChecked={
								formData?.unionMember === false ||
								formData?.unionMember === "no"
							}
							{...register("unionMember", {
								required: false,
							})}
							type="radio"
							id="unionMember-no"
							value="no"
						/>
						<label htmlFor="unionMember-no">No</label>
					</div>
				</div>
			</div>

			<div className="w-full flex justify-end mt-8">
				<button
					onClick={handleSubmit(onSubmit)}
					className="bg-primary-mango text-white h-14 px-8 rounded-lg text-sm"
				>
					Continue
				</button>
			</div>
		</form>
	);
};

export default PersonalDetails;